import { Injectable } from '@angular/core';
import { AuthService } from '../../auth/auth.service';
import { CrudPermissions, CrudPermissionValue, Permission } from './permission.model';
import { User } from '../user/user.model';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Account } from '../accounts/account.model';

@Injectable({
  providedIn: 'root'
})
export class PermissionsService {

  private permissions: Permission[];

  constructor(private authService: AuthService) {
    this.permissions = this.handleData(this.authService.user);
    this.authService.user$.subscribe(data => {
      this.permissions = this.handleData(data);
    });
  }

  /**
   * Procesar los datos de los permisos del usuario que vienen del server
   *
   * @param data
   */
  handleData(data: User): Permission[] {
    const arrPermissions: Permission[] = [];
    // Verificar si los permisos vienen como array plano (estructura nueva)
    if (Array.isArray(data)) {
      // Si data es un array de strings, convertirlo a objetos Permission
      data.forEach(permissionId => {
        if (typeof permissionId === 'string') {
          arrPermissions.push({ id: permissionId } as Permission);
        } else if (permissionId.id) {
          arrPermissions.push(permissionId);
        }
      });
    }
    else if (data?.accounts !== undefined) {
      for (const account of data.accounts) {
        // Agrega este console.log para ver qué tipo de cuenta tiene el doctor
        console.log('CUENTA DEL USUARIO:', account.account_type_id, 'TIPO DE USUARIO EN PIVOT:', account.pivot?.account_user_type_id);

        for (const permission of account.pivot.user_type.permissions) {
          arrPermissions.push(permission);
        }
      }
    }

    // Agrega este console.log para ver TODOS los permisos que cree que tiene
    console.log('PERMISOS TOTALES CARGADOS EN EL SERVICIO:', arrPermissions.map(p => p.id));

    return arrPermissions;
  }


  accountsWithPermission(permissionToCheck: string): Account[] {

    const data = this.authService.user;

    const accPermissions: Account[] = [];
    if (data.accounts !== undefined) {
      for (const account of data.accounts) {
        const filtered = account.pivot.user_type.permissions.filter(permission => permission.id === permissionToCheck);
        if (filtered.length > 0) {
          accPermissions.push(account);
        }
      }
    }
    return accPermissions;
  }

  /**
   * Comprobar si el usuario tiene ciertos permisos
   *
   * @param permissionsQueried
   * @param queryType
   */
  hasPermission(permissionsQueried: string[], queryType: string = Permission.AND_QUERY): boolean {

    // Se hace filter del array de permisos
    const permissions = this.permissions.filter(permission => permissionsQueried.includes(permission.id));

    // Si no tiene luego permisos ya se retorna false
    if (permissions.length === 0) {
      return false;
    }

    // Si tiene algunos permisos pero no todos, y es un AND, se retorna false
    if (permissions.length < permissionsQueried.length && queryType === Permission.AND_QUERY) {
      return false;
    }

    // Sino se retorna true
    return true;
  }

  /**
   * Metodo para comprobar los permisos para los ABMs
   *
   * @param path
   * @param type
   * @param configP
   * @private
   */
  private crudPermissionsCheck(path: string, type: string, configP: CrudPermissions): boolean {

    // Si el path no comienza con el type se retorna null ya de entrada
    if (path === '' || path.indexOf(type) !== 0) {
      return null;
    }

    // Listado
    if (path === type) {
      return this.hasPermission(configP.list.permissions, configP.list.typeCondition);
    }

    // Agregar
    if (path === type + '/add') {
      return this.hasPermission(configP.add.permissions, configP.add.typeCondition);
    }

    // Eliminar
    if (new RegExp('^' + type + '\/.*[\/]?delete').test(path)) {
      return this.hasPermission(configP.delete.permissions, configP.delete.typeCondition);
    }

    // Editar
    if (new RegExp('^' + type + '\/.*[\/]?edit$').test(path)) {
      return this.hasPermission(configP.edit.permissions, configP.edit.typeCondition);
    }

    // Ver
    if (new RegExp('^' + type + '\\/.+$').test(path)) {
      return this.hasPermission(configP.view.permissions, configP.view.typeCondition);
    }

    return null;
  }

  /**
   * Permisos por ruta para el usuario
   *
   * @param route
   * @param state
   */
  routePermissions(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {

    const currentUrl = state.url.replace('/app/', '');
    return this.pathPermission(currentUrl);
  }

  /**
   * Se verifican si se tienen permisos en un path especifico
   *
   * @param path
   */
  pathPermission(path: string): boolean {

    // Se retorna true en caso de paginas publicas
    if (['forbidden', 'dashboard'].includes(path)) {
      return true;
    }

    // Se retorna true en caso de tener para cambiar el tipo de panel
    if (path.indexOf('/mode-selector') === 0) {

      // Se verifica si es doctor o administrador de sistema
      for (const account of this.authService.user.accounts) {
        // Si es doctor o administrador
        if (['medical_center', 'sys_admin'].includes(account.account_type_id) && account.pivot.account_user_type_id !== 'patient') {
          return true;
        }
      }

      return true;
    }

    // Listado de accounts donde el user es admin
    const accountAdminList: Account[] = this.authService.user.accounts.filter((account) => account.pivot.account_user_type_id === 'administrator' && account.account_type_id !== 'patient'
    );


    // --- Permisos para paginas de Validaciones de Usuarios -- **/
    if (path.indexOf('user-validation/') === 0) {
      return true;
    }

    // --- Permisos para paginas de ABM de usuarios -- **/

    if (this.crudPermissionsCheck(path, 'users', new CrudPermissions()
      // .onView(new CrudPermissionValue(['view_user_global', 'view_user_own']))
      .onList(new CrudPermissionValue(['view_user_global', 'view_user_own']))
      .onAdd(new CrudPermissionValue(['add_user_global', 'add_user_own']))
      .onEdit(new CrudPermissionValue(['edit_user_global', 'edit_user_own']))
      .onDelete(new CrudPermissionValue(['del_user_global', 'del_user_own']))
    )) {

      // Solo si tiene alguna otra cuenta que no sea la individual se le muestra para agregar usuarios (adherentes)
      return User.filterIndividualAccounts(this.authService.user.accounts)
        .filter(data => data.account_type_id === 'company' && data.pivot.account_user_type_id === 'administrator').length > 0
        || User.filterIndividualAccounts(this.authService.user.accounts).filter(data => data.id.toString() === '1').length > 0;
    }


    // --- Permisos para paginas de ABM de Especialidades médicas -- **/

    if (this.crudPermissionsCheck(path, 'medical-specialities', new CrudPermissions()
      // .onView(new CrudPermissionValue(['view_crud_med_spec']))
      .onList(new CrudPermissionValue(['view_crud_med_spec']))
      .onAdd(new CrudPermissionValue(['add_med_speciality']))
      .onEdit(new CrudPermissionValue(['edit_med_speciality']))
      .onDelete(new CrudPermissionValue(['del_med_speciality']))
    )) {
      return true;
    }


    // --- Permisos para paginas de ABM de Estudios Medicos -- **/

    if (this.crudPermissionsCheck(path, 'medical-studies', new CrudPermissions()
      // .onView(new CrudPermissionValue(['view_crud_med_spec']))
      .onList(new CrudPermissionValue(['view_crud_med_study', 'list_all_med_studies'], Permission.AND_QUERY))
      .onView(new CrudPermissionValue(['view_crud_med_study', 'list_all_med_studies'], Permission.AND_QUERY))
      .onAdd(new CrudPermissionValue(['add_med_studies']))
      .onEdit(new CrudPermissionValue(['edit_med_studies']))
      .onDelete(new CrudPermissionValue(['del_med_studies']))
    )) {
      return true;
    }


    // --- Permisos para paginas de Validaciones -- **/

    if (this.crudPermissionsCheck(path, 'validations', new CrudPermissions()
      .onView(new CrudPermissionValue(['approve_validation', 'reject_validation']))
      .onList(new CrudPermissionValue(['view_validation_list']))
    )) {
      return true;
    }

    if (path === 'validations/approve' && this.hasPermission(['approve_validation'])) {
      return true;
    }

    if (path === 'validations/reject' && this.hasPermission(['reject_validation'])) {
      return true;
    }


    // --- Permisos para paginas de Doctores -- **/

    if (
      User.filterIndividualAccounts(this.authService.user.accounts)
        .filter(data => data.configurations?.enabled_for_adding_doctors).length > 0 &&
      this.crudPermissionsCheck(path, 'doctors', new CrudPermissions()
        // .onView(new CrudPermissionValue(['view_doctor_global', 'view_doctor_own']))
        .onList(new CrudPermissionValue(['view_doctor_global', 'view_doctor_own']))
        .onAdd(new CrudPermissionValue(this.authService.panelType !== 'assistant' ? ['add_doctor_global', 'add_doctor_own'] : ['NOPERM']))
        .onEdit(
          new CrudPermissionValue(this.authService.panelType !== 'assistant' ? ['edit_doctor_global', 'edit_doctor_own'] : ['NOPERM']))
        .onDelete(
          new CrudPermissionValue(this.authService.panelType !== 'assistant' ? ['del_doctor_global', 'del_doctor_own'] : ['NOPERM']))
      )) {
      return true;
    }

    if (path.indexOf('doctors/configurations') === 0 && this.authService.panelType === 'doctor') {
      return this.hasPermission(['modify_doctor_conf'])
        && User.filterIndividualAccounts(this.authService.user.accounts)
          .filter(data => data.pivot.account_user_type_id === 'doctor').length > 0;
    }


    // --- Permisos para paginas de ABM de Asistentes -- **/

    if (this.crudPermissionsCheck(path, 'assistants', new CrudPermissions()
      // .onView(new CrudPermissionValue(['list_own_assistants', 'list_acc_assistants', 'list_glob_assistants']))
      .onList(new CrudPermissionValue(['list_own_assistants', 'list_acc_assistants', 'list_glob_assistants']))
      .onAdd(new CrudPermissionValue(['add_own_assistants', 'add_acc_assistants', 'add_glob_assistants']))
      .onEdit(new CrudPermissionValue(['edit_acc_assistant', 'edit_glob_assistant']))
      .onDelete(new CrudPermissionValue(['del_own_assistants', 'del_acc_assistants', 'del_glob_assistants']))
    )) {

      // Si es que es company, no se muestra
      if (accountAdminList.filter((account) => account.account_type_id !== 'company').length !== 0) {
        return true;
      }
    }


    // --- Permisos para paginas de ABM de Configuraciones del sistema -- **/

    if (this.crudPermissionsCheck(path, 'system-configurations', new CrudPermissions()
      .onView(new CrudPermissionValue(['list_all_conf']))
      .onList(new CrudPermissionValue(['list_all_conf']))
      .onAdd(new CrudPermissionValue(['add_conf']))
      .onEdit(new CrudPermissionValue(['edit_conf']))
      .onDelete(new CrudPermissionValue(['del_conf']))
    )) {
      return true;
    }


    // --- Permisos para paginas de ABM de Unidades de medida -- **/

    if (this.crudPermissionsCheck(path, 'measurements', new CrudPermissions()
      .onView(new CrudPermissionValue(['view_crud_measure', 'list_all_measure'], Permission.AND_QUERY))
      .onList(new CrudPermissionValue(['view_crud_measure', 'list_all_measure'], Permission.AND_QUERY))
      .onAdd(new CrudPermissionValue(['add_measure']))
      .onEdit(new CrudPermissionValue(['edit_measure']))
      .onDelete(new CrudPermissionValue(['del_measure']))
    )) {
      return true;
    }


    // --- Permisos para paginas de ABM de Laboratorios -- **/

    if (this.crudPermissionsCheck(path, 'laboratories', new CrudPermissions()
      .onView(new CrudPermissionValue(['view_crud_laboratory', 'list_all_laboratory'], Permission.AND_QUERY))
      .onList(new CrudPermissionValue(['view_crud_laboratory', 'list_all_laboratory'], Permission.AND_QUERY))
      .onAdd(new CrudPermissionValue(['add_laboratory']))
      .onEdit(new CrudPermissionValue(['edit_laboratory']))
      .onDelete(new CrudPermissionValue(['del_laboratory']))
    )) {
      return true;
    }

    // --- Permisos para paginas de ABM de centro de diagnosticos -- **/

    if (this.crudPermissionsCheck(path, 'diagnostic-center', new CrudPermissions()
      .onView(new CrudPermissionValue(['view_crud_diagnostic_center', 'list_all_diagnostic_center'], Permission.AND_QUERY))
      .onList(new CrudPermissionValue(['view_crud_diagnostic_center', 'list_all_diagnostic_center'], Permission.AND_QUERY))
      .onAdd(new CrudPermissionValue(['add_diagnostic_center']))
      .onEdit(new CrudPermissionValue(['edit_diagnostic_center']))
      .onDelete(new CrudPermissionValue(['del_diagnostic_center']))
    )) {
      return true;
    }


    if (this.crudPermissionsCheck(path, 'laboratorio-protesis', new CrudPermissions()
      .onView(new CrudPermissionValue(['view_crud_laboratorio_protesis', 'list_all_laboratorio_protesis'], Permission.AND_QUERY))
      .onList(new CrudPermissionValue(['view_crud_laboratorio_protesis', 'list_all_laboratorio_protesis'], Permission.AND_QUERY))
      .onAdd(new CrudPermissionValue(['add_laboratorio_protesis']))
      .onEdit(new CrudPermissionValue(['edit_laboratorio_protesis']))
      .onDelete(new CrudPermissionValue(['del_laboratorio_protesis']))
    )) {
      return true;
    }



    // --- Permisos para paginas de ABM de Solicitud de Créditos ---
    if (this.crudPermissionsCheck(path, 'solicit-credit', new CrudPermissions()
      .onView(new CrudPermissionValue(['view_crud_solicit_credit', 'list_solicit_credit', 'list_all_solicit_credit'], Permission.OR_QUERY)) // ✅ CLAVE PARA EL BOTÓN VER
      .onList(new CrudPermissionValue(['list_solicit_credit', 'list_all_solicit_credit'], Permission.OR_QUERY))
      .onAdd(new CrudPermissionValue(['add_solicit_credit']))
      .onEdit(new CrudPermissionValue(['edit_solicit_credit']))
      .onDelete(new CrudPermissionValue(['del_solicit_credit']))
    )) {
      return true;
    }

    // --- Permisos para paginas de ABM de Planes de Doctores ---
    if (this.crudPermissionsCheck(path, 'plan-doctors', new CrudPermissions()
      .onView(new CrudPermissionValue(['view_crud_plan_doctors', 'list_plan_doctors', 'list_all_plan_doctors'], Permission.OR_QUERY)) // ✅ CLAVE PARA EL BOTÓN VER
      .onList(new CrudPermissionValue(['list_plan_doctors', 'list_all_plan_doctors'], Permission.OR_QUERY))
      .onAdd(new CrudPermissionValue(['add_plan_doctors']))
      .onEdit(new CrudPermissionValue(['edit_plan_doctors']))
      .onDelete(new CrudPermissionValue(['del_plan_doctors']))
    )) {
      return true;
    }

    // --- Permisos para paginas de ABM de Solicitud de Prótesis ---
    if (this.crudPermissionsCheck(path, 'solicitud-protesis', new CrudPermissions()
      .onView(new CrudPermissionValue(['view_crud_solicitud_protesis', 'list_solicitud_protesis', 'list_all_solicitud_protesis'], Permission.OR_QUERY)) // ✅ CLAVE PARA EL BOTÓN VER
      .onList(new CrudPermissionValue(['list_solicitud_protesis', 'list_all_solicitud_protesis'], Permission.OR_QUERY))
      .onAdd(new CrudPermissionValue(['add_solicitud_protesis']))
      .onEdit(new CrudPermissionValue(['edit_solicitud_protesis']))
      .onDelete(new CrudPermissionValue(['del_solicitud_protesis']))
    )) {
      return true;
    }

    // --- Permisos para paginas de ABM de Solicitud de Prótesis ---
    if (this.crudPermissionsCheck(path, 'consultas', new CrudPermissions()
      .onView(new CrudPermissionValue(['view_crud_consultas', 'list_consultas', 'list_all_consultas'], Permission.OR_QUERY)) // ✅ CLAVE PARA EL BOTÓN VER
      .onList(new CrudPermissionValue(['list_consultas', 'list_all_consultas'], Permission.OR_QUERY))
      .onAdd(new CrudPermissionValue(['add_consultas']))
      .onEdit(new CrudPermissionValue(['edit_consultas']))
      .onDelete(new CrudPermissionValue(['del_consultas']))
    )) {
      return true;
    }

    // --- Permisos para paginas de ABM de Pagopar Transacciones ---
    if (this.crudPermissionsCheck(path, 'pagopar-transactions', new CrudPermissions()
      .onView(new CrudPermissionValue(['view_crud_pagopar_transactions', 'list_pagopar_transactions', 'list_all_pagopar_transactions'], Permission.OR_QUERY)) // ✅ CLAVE PARA EL BOTÓN VER
      .onList(new CrudPermissionValue(['list_pagopar_transactions', 'list_all_pagopar_transactions'], Permission.OR_QUERY))
      .onAdd(new CrudPermissionValue(['add_pagopar_transactions']))
      .onEdit(new CrudPermissionValue(['edit_pagopar_transactions']))
      .onDelete(new CrudPermissionValue(['del_pagopar_transactions']))
    )) {
      return true;
    }

    // --- Permisos para paginas de ABM de Solicitud de Prótesis ---
    if (this.crudPermissionsCheck(path, 'banks', new CrudPermissions()
      .onView(new CrudPermissionValue(['view_crud_banks', 'list_banks', 'list_all_banks'], Permission.OR_QUERY)) // ✅ CLAVE PARA EL BOTÓN VER
      .onList(new CrudPermissionValue(['list_banks', 'list_all_banks'], Permission.OR_QUERY))
      .onAdd(new CrudPermissionValue(['add_banks']))
      .onEdit(new CrudPermissionValue(['edit_banks']))
      .onDelete(new CrudPermissionValue(['del_banks']))
    )) {
      return true;
    }

    // --- Permisos para paginas de ABM de Presupuestos ---
    if (this.crudPermissionsCheck(path, 'presupuestos', new CrudPermissions()
      .onView(new CrudPermissionValue(['view_crud_presupuestos', 'list_presupuestos', 'list_all_presupuestos'], Permission.OR_QUERY)) // ✅ CLAVE PARA EL BOTÓN VER
      .onList(new CrudPermissionValue(['list_presupuestos', 'list_all_presupuestos'], Permission.OR_QUERY))
      .onAdd(new CrudPermissionValue(['add_presupuestos']))
      .onEdit(new CrudPermissionValue(['edit_presupuestos']))
      .onDelete(new CrudPermissionValue(['del_presupuestos']))
    )) {
      return true;
    }


    // --- Permisos para paginas de ABM de Cuentas Bancarias Doctores -- **/

    if (this.crudPermissionsCheck(path, 'doctor-bank-account', new CrudPermissions()
      .onView(new CrudPermissionValue(['view_crud_doctor_bank_account', 'list_all_doctor_bank_account'], Permission.AND_QUERY))
      .onList(new CrudPermissionValue(['view_crud_doctor_bank_account', 'list_all_doctor_bank_account'], Permission.AND_QUERY))
      .onAdd(new CrudPermissionValue(['add_doctor_bank_account']))
      .onEdit(new CrudPermissionValue(['edit_doctor_bank_account']))
      .onDelete(new CrudPermissionValue(['del_doctor_bank_account']))
    )) {
      return true;
    }

    // --- Permisos para paginas de ABM de Diagnosticos -- **/

    if (this.crudPermissionsCheck(path, 'diagnoses', new CrudPermissions()
      .onView(new CrudPermissionValue(['view_crud_diagnoses', 'list_all_diagnoses'], Permission.AND_QUERY))
      .onList(new CrudPermissionValue(['view_crud_diagnoses', 'list_all_diagnoses'], Permission.AND_QUERY))
      .onAdd(new CrudPermissionValue(['add_diagnoses']))
      .onEdit(new CrudPermissionValue(['edit_diagnoses']))
      .onDelete(new CrudPermissionValue(['del_diagnoses']))
    )) {
      return true;
    }

    // --- Permisos para paginas de ABM de Medicinas -- **/

    if (this.crudPermissionsCheck(path, 'medicines', new CrudPermissions()
      .onView(new CrudPermissionValue(['view_crud_medicine', 'list_all_medicine'], Permission.AND_QUERY))
      .onList(new CrudPermissionValue(['view_crud_medicine', 'list_all_medicine'], Permission.AND_QUERY))
      .onAdd(new CrudPermissionValue(['add_medicine']))
      .onEdit(new CrudPermissionValue(['edit_medicine']))
      .onDelete(new CrudPermissionValue(['del_medicine']))
    )) {
      return true;
    }


    // --- Permisos para paginas de ABM de Planes -- **/

    //if (this.crudPermissionsCheck(path, 'plans/plan-configurations', new CrudPermissions()
    //  .onView(new CrudPermissionValue(['view_crud_plans', 'list_all_plans'], Permission.AND_QUERY))
    //  .onList(new CrudPermissionValue(['view_crud_plans', 'list_all_plans'], Permission.AND_QUERY))
    //  .onAdd(new CrudPermissionValue(['add_plans']))
    //  .onEdit(new CrudPermissionValue(['edit_plans']))
    //  .onDelete(new CrudPermissionValue(['del_plans']))
    //)) {
    //  return true;
    //}

    // --- Permisos para paginas de planes para usuarios finales -- **/
    if (path.indexOf('plans') > -1 && path.indexOf('/plan-config') < 0 &&
      this.hasPermission(['view_hire_plans', 'view_credits', 'renounce_plan'], Permission.OR_QUERY)
    ) {
      return true;
    }


    // --- Permisos para paginas de ABM de Accounts -- **/

    if (path.indexOf('accounts') === 0) {
      // Solo el admin de mediget puede ver las cuentas
      return User.filterIndividualAccounts(this.authService.user.accounts)
        .filter(data => data.id.toString() === '1').length > 0 && this.crudPermissionsCheck(path, 'accounts', new CrudPermissions()
          .onView(new CrudPermissionValue(['view_accounts']))
          .onList(new CrudPermissionValue(['view_accounts']))
          .onAdd(new CrudPermissionValue(['add_accounts']))
          .onEdit(new CrudPermissionValue(['edit_accounts']))
          .onDelete(new CrudPermissionValue(['del_accounts']))
        );
    }


    // --- Permisos para paginas de ABM de Pacientes -- **/

    if (this.crudPermissionsCheck(path, 'patients', new CrudPermissions()
      .onView(new CrudPermissionValue(['view_patient_global', 'view_patient_own']))
      .onList(new CrudPermissionValue(['view_patient_global', 'view_patient_own']))
      .onAdd(new CrudPermissionValue(['add_patient_global', 'add_patient_own']))
      .onEdit(new CrudPermissionValue(['edit_patient_global', 'edit_patient_own']))
      .onDelete(new CrudPermissionValue(['del_patient_global', 'del_patient_own']))
    )) {

      // Si es que es company, no se muestra
      if (accountAdminList.filter((account) => account.account_type_id !== 'company').length !== 0) {
        return true;
      }
    }


    // --- Permisos para paginas de Agendamientos -- **/

    if (['schedule/view', 'schedule'].includes(path) &&
      this.hasPermission(['list_own_schedules', 'list_all_schedules'], Permission.OR_QUERY)) {

      // Si es que es company, no se muestra
      if (accountAdminList.filter((account) => account.account_type_id !== 'company').length !== 0) {
        return true;
      }
    }

    // Si es doctor y aun no tiene seteado el precio de consulta, no se le habilita el menu
    if (path.indexOf('schedule/config') === 0 && this.authService.user.doctor?.consultation_price === null) {
      return false;
    }

    // Si no es paciente no se le habilita la busqueda de doctores para calendarizar
    if (path.indexOf('schedule/search') === 0 && this.authService.panelType !== 'patient') {
      return false;
    }

    if (this.crudPermissionsCheck(path, 'schedule/config', new CrudPermissions()
      // .onView(new CrudPermissionValue(['list_all_schedules', 'list_own_schedules']))
      .onList(new CrudPermissionValue(['list_all_schedules', 'list_own_schedules']))
      .onAdd(new CrudPermissionValue(['add_global_schedule', 'add_own_schedule']))
      .onEdit(new CrudPermissionValue(['edit_global_schedule', 'edit_own_schedule']))
      .onDelete(new CrudPermissionValue(['del_global_schedules', 'del_own_schedules']))
    )) {

      // Si es que es company, no se muestra
      if (accountAdminList.filter((account) => account.account_type_id !== 'company').length !== 0) {
        return true;
      }
    }


    // --- Permisos para paginas de Seguros medicos -- **/

    // if (this.crudPermissionsCheck(path, 'insurances', new CrudPermissions()
    //   .onView(new CrudPermissionValue(['view_glob_med_ins']))
    //   .onList(new CrudPermissionValue(['view_glob_med_ins']))
    //   .onAdd(new CrudPermissionValue(['add_med_insurance']))
    //   .onEdit(new CrudPermissionValue(['edit_med_insurance']))
    //   .onDelete(new CrudPermissionValue(['del_med_insurance']))
    // )) {
    //   return true;
    // }


    /** --- Permisos para Metodos de pago --- */

    // Ocultar facturacion para asistente
    if (path.indexOf('billing') === 0 && this.authService.panelType === 'assistant') {
      return false;
    }

    // ABM de metodo de pago
    if (this.crudPermissionsCheck(path, 'billing/payment-methods', new CrudPermissions()
      .onList(new CrudPermissionValue(['view_payment_mth', 'adm_payment_mth'], Permission.AND_QUERY))
      .onAdd(new CrudPermissionValue(['add_payment_mth']))
      .onEdit(new CrudPermissionValue(['edit_payment_mth']))
      .onDelete(new CrudPermissionValue(['del_payment_mth']))
    )) {
      return true;
    }


    /** --- Permisos para ver consultas --- */

    if (path.indexOf('consultation') === 0) {

      return !(path.indexOf('/quick-creation') > -1 && !this.hasPermission(['add_consultation_otf']));

    }


    /** --- Permisos para ver prescripciones --- */

    if (path.indexOf('prescriptions') === 0 && path.indexOf('add') < 0 && path.indexOf('edit') < 0 && path.indexOf('delete') < 0) {

      return true;

    }


    /** --- Permisos para ver ficha medica --- */

    if (path.indexOf('medical-records') === 0 && this.authService.panelType === 'patient') {
      return true;
    }


    /** --- Permisos para notificaciones --- */

    if (path.indexOf('notifications') === 0) {
      return true;
    }


    /** --- Permisos para buscar schedules --- */

    if (path.indexOf('schedule/search') === 0) {
      return true;
    }


    /** --- Permisos para transcripciones --- */

    if (path.indexOf('transcriptions') === 0 && this.hasPermission(['list_transcriptions'])) {
      return true;
    }

    if (path.indexOf('diagnostic_center') === 0 && this.hasPermission(['list_diagnostic_center'])) {
      return true;
    }

    if (path.indexOf('laboratorio_protesis') === 0 && this.hasPermission(['list_laboratorio_protesis'])) {
      return true;
    }



    if (path.indexOf('solicit_credit') === 0 && this.hasPermission(['list_solicit_credit'])) {
      return true;
    }

    if (path.indexOf('solicitud_protesis') === 0 && this.hasPermission(['list_solicitud_protesis'])) {
      return true;
    }

    if (path.indexOf('plan_doctors') === 0 && this.hasPermission(['list_plan_doctors'])) {
      return true;
    }

    if (path.indexOf('presupuestos') === 0 && this.hasPermission(['list_presupuestos'])) {
      return true;
    }

    if (path.indexOf('doctor_bank_account') === 0 && this.hasPermission(['list_doctor_bank_account'])) {
      return true;
    }

    /** --- Permisos para ver cuenta propia de usuario --- */

    if (path.indexOf('profile') === 0) {
      return true;
    }

    /** --- Permisos para procesamiento de agregado de tarjeta --- */

    if (path.indexOf('billing/added-card') === 0) {
      return true;
    }

    /** --- Permisos para recibos de pago --- */

    if (
      (
        path.search(/billing\/invoices\/.*view/) === 0 ||
        path.search(/billing\/invoices\/[0-9]+$/) === 0
      ) &&
      this.hasPermission(['view_all_invoices', 'view_acc_invoices'], Permission.OR_QUERY)) {
      return true;
    }

    if (path.search(/billing\/invoices$/) === 0 &&
      this.hasPermission(['list_acc_invoices', 'list_all_invoices'], Permission.OR_QUERY)) {
      return true;
    }

    /** --- Permisos para reportes --- */

    if (path.indexOf('reports') === 0) {

      // Reporte de facturacion no aparece en asistentes
      if (path.includes('reports/invoices') && this.authService.panelType === 'assistant') {
        return false;
      }

      // Reporte de facturacion no aparece en asistentes
      if (path.includes('reports/consultations') && this.authService.panelType === 'assistant') {
        return false;
      }

      // Reportes de diagnosticos no aparece en panel de asistente ni de pacientes
      if (path.includes('reports/diagnoses') && ['assistant', 'patient'].includes(this.authService.panelType)) {
        return false;
      }

      // Reportes de diagnosticos no aparece en panel de asistente ni de pacientes
      if (path.includes('reports/patients-treated') && (!['admin'].includes(this.authService.panelType) || !this.hasPermission(['see_account_consult']) || this.hasPermission(['see_global_consult']) ||
        // Si es que es company, no se muestra
        accountAdminList.filter((account) => account.account_type_id !== 'company').length === 0)) {
        return false;
      }

      if (path.match(/reports\/prescriptions\/.*\/amount$/) != null && !this.hasPermission(['see_amount_glob_pres', 'see_amount_acc_pres', 'see_amount_own_pres'], Permission.OR_QUERY)) {
        return false;
      }

      if (path.match(/reports\/prescriptions\/.*\/detailed/) != null &&
        (!['doctor'].includes(this.authService.panelType) ||
          !this.hasPermission(['see_global_pres', 'see_account_pres', 'see_own_pres'], Permission.OR_QUERY))) {
        return false;
      }

      //
      return true;
    }


    // Si no tiene los permisos de arriba, se retorna false

    return false;
  }

}
